import React from 'react';
import { Coins } from 'lucide-react';
import { useCoins } from '@/hooks/useCoins';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { motion, useReducedMotion } from 'framer-motion';
import { AnimatedNumber } from '@/lib/motion';
import { springSnappy } from '@/lib/motion/presets';

/**
 * Compact coin balance chip for the dashboard header.
 * Click → /earn.
 */
export const CoinWalletChip: React.FC = () => {
  const { user } = useAuth();
  const { balance, isLoading } = useCoins();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  // Signed-out visitors have no wallet — never show a balance to them.
  if (!user || isLoading) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => navigate('/earn')}
      className="h-8 gap-1.5 px-2 rounded-full bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20"
      aria-label={`${balance} coins — go to Earn Hub`}
    >
      <motion.span
        key={balance}
        initial={reducedMotion ? false : { scale: 0.75, rotate: -12 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={springSnappy}
      >
        <Coins className="h-3.5 w-3.5 text-amber-500" />
      </motion.span>
      <AnimatedNumber
        value={balance}
        grouped
        className="font-mono text-xs font-semibold tabular-nums text-amber-200"
      />
    </Button>
  );
};